import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import ScrapeProgress from '../components/ScrapeProgress';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const MAX_PX = 1200;
const JPEG_QUALITY = 0.82;

// ---- shared scrape hook ----

function useScraper(getToken) {
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const s = await fetch(`${API}/scrape-status`).then(r => r.json());
        setScrapeStatus(s);
        if (!s.inProgress) stopPolling();
      } catch { stopPolling(); }
    }, 2000);
  }, []);

  useEffect(() => () => stopPolling(), []);

  async function trigger(stores = null) {
    try {
      const res = await fetch(`${API}/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(stores ? { stores } : {}),
      });
      const data = await res.json();
      if (!res.ok) { setScrapeStatus({ inProgress: false, logs: [{ time: new Date().toISOString(), message: `Error: ${data.message}` }] }); return; }
      setScrapeStatus({ inProgress: true, startedAt: new Date().toISOString(), logs: [] });
      startPolling();
    } catch (err) {
      setScrapeStatus({ inProgress: false, logs: [{ time: new Date().toISOString(), message: `Error: ${err.message}` }] });
    }
  }

  async function cancel() {
    await fetch(`${API}/scrape/cancel`, { method: 'POST' }).catch(() => {});
  }

  return { scrapeStatus, trigger, cancel };
}

// ---- image helpers ----

function compressImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, MAX_PX / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(blob => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }, 'image/jpeg', JPEG_QUALITY);
    };
    img.onerror = reject;
    img.src = url;
  });
}

const SOURCE_LABEL = { anthropic: 'Claude Haiku AI', tesseract: 'Tesseract OCR (fallback)' };
const SOURCE_CLASS  = { anthropic: 'badge--blue', tesseract: 'badge--yellow' };

export default function AdminPage() {
  const { getToken } = useAuth();
  const { scrapeStatus, trigger, cancel } = useScraper(getToken);
  const fileInputRef = useRef(null);

  const [imagePreview, setImagePreview] = useState(null);
  const [imageData, setImageData]       = useState(null);
  const [extracting, setExtracting]         = useState(false);
  const [extractedDeals, setExtractedDeals] = useState(null);
  const [rawExtracted, setRawExtracted]     = useState(null);
  const [source, setSource]                 = useState('');
  const [applying, setApplying]             = useState(false);
  const [msg, setMsg]     = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  function reset() {
    setExtractedDeals(null); setRawExtracted(null); setSource('');
    setMsg(''); setError(''); setCopied(false);
  }

  async function handleFile(file) {
    if (!file) return;
    reset();
    setImagePreview(URL.createObjectURL(file));
    setImageData(null);
    try { setImageData(await compressImage(file)); }
    catch { setError('Could not read image file.'); }
  }

  function handleDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  async function handleExtract() {
    if (!imageData) return;
    setExtracting(true); setError(''); setMsg('');
    try {
      const res = await fetch(`${API}/admin/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ imageData, mimeType: 'image/jpeg' }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Extraction failed.'); return; }
      setExtractedDeals(data.deals); setRawExtracted(data.extracted); setSource(data.source || '');
      const fallbackNote = data.aiError ? ` (AI error: ${data.aiError})` : '';
      setMsg(`Extracted ${data.deals.length} deal${data.deals.length !== 1 ? 's' : ''}. Review below, then click Apply.${fallbackNote}`);
    } catch (err) { setError(err.message); }
    finally { setExtracting(false); }
  }

  async function handleApply() {
    if (!extractedDeals) return;
    setApplying(true); setError(''); setMsg('');
    try {
      const store = rawExtracted?.supermarket || 'Other';
      const res = await fetch(`${API}/admin/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ deals: extractedDeals, store }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Apply failed.'); return; }
      setMsg(data.message); reset(); setImagePreview(null); setImageData(null);
    } catch (err) { setError(err.message); }
    finally { setApplying(false); }
  }

  function handleCopyJson() {
    if (!rawExtracted) return;
    navigator.clipboard.writeText(JSON.stringify(rawExtracted, null, 2));
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  }

  function removeExtractedDeal(index) {
    setExtractedDeals(prev => prev.filter((_, i) => i !== index));
  }

  const sourceKey = source.replace(' (cached)', '');
  const isRunning = scrapeStatus?.inProgress;

  return (
    <div className="admin-page">
      <h2 className="admin-title">Admin Panel</h2>

      {/* ── Scrape All ── */}
      <section className="admin-section">
        <h3>Scrape All Stores</h3>
        <p className="admin-hint">
          Runs all scrapers: Coles, Woolworths, Aldi Super Savers, Aldi Special Buys, and Chemist Warehouse.
          Scheduled automatically every Wednesday and Saturday at 7 AM AEST.
        </p>
        <div className="admin-actions">
          <button
            className="btn-admin-scrape-all"
            onClick={() => trigger(null)}
            disabled={isRunning}
          >
            {isRunning ? 'Scraping…' : 'Scrape All Stores Now'}
          </button>
          {!isRunning && (
            <button
              className="btn-admin-extract"
              onClick={() => trigger(['aldi-specials'])}
              disabled={isRunning}
            >
              Refresh Aldi Specials Only
            </button>
          )}
        </div>
        <ScrapeProgress status={scrapeStatus} onCancel={cancel} />
      </section>

      {/* ── Screenshot Import ── */}
      <section className="admin-section">
        <h3>Screenshot Import</h3>
        <p className="admin-hint">
          Upload a Coles, Woolworths, Aldi, or Costco deals screenshot.
          Claude AI extracts products automatically.
          Tesseract OCR is used as fallback if AI is unavailable.
        </p>

        <div
          className={`admin-dropzone ${imagePreview ? 'admin-dropzone--has-image' : ''}`}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
        >
          {imagePreview ? (
            <img src={imagePreview} alt="Screenshot preview" className="admin-preview-img" />
          ) : (
            <div className="admin-dropzone-placeholder">
              <span className="admin-dropzone-icon">📷</span>
              <p>Drop a screenshot here, or click to choose</p>
              <p className="admin-dropzone-hint">PNG · JPG · WebP — compressed automatically before upload</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0])}
          />
        </div>

        <div className="admin-actions">
          <button className="btn-admin-extract" onClick={handleExtract} disabled={!imageData || extracting}>
            {extracting ? 'Extracting…' : 'Extract Products'}
          </button>
          {extractedDeals && (
            <button className="btn-admin-apply" onClick={handleApply} disabled={applying || extractedDeals.length === 0}>
              {applying ? 'Saving…' : `Apply ${extractedDeals.length} Deals to Site`}
            </button>
          )}
          {rawExtracted && (
            <button className="btn-admin-copy" onClick={handleCopyJson}>
              {copied ? '✓ Copied' : 'Copy JSON'}
            </button>
          )}
        </div>

        {source && (
          <div className="admin-source-row">
            <span className="admin-source-label">Extracted by</span>
            <span className={`badge ${SOURCE_CLASS[sourceKey] || 'badge--muted'}`}>
              {SOURCE_LABEL[sourceKey] || source}
            </span>
            {rawExtracted?.supermarket && rawExtracted.supermarket !== 'Unknown' && (
              <span className="badge badge--muted">{rawExtracted.supermarket}</span>
            )}
            {rawExtracted?.catalog_type && rawExtracted.catalog_type !== 'other' && (
              <span className="badge badge--muted">{rawExtracted.catalog_type}</span>
            )}
            {rawExtracted?.valid_until && (
              <span className="admin-valid-until">Valid until {rawExtracted.valid_until}</span>
            )}
          </div>
        )}

        {msg   && <p className="admin-msg admin-msg--success">{msg}</p>}
        {error && <p className="admin-msg admin-msg--error">{error}</p>}
      </section>

      {extractedDeals && extractedDeals.length > 0 && (
        <section className="admin-section">
          <h3>Preview — {extractedDeals.length} deal{extractedDeals.length !== 1 ? 's' : ''} found</h3>
          <p className="admin-hint">Remove incorrect items before applying.</p>
          <div className="admin-deals-table-wrap">
            <table className="admin-deals-table">
              <thead>
                <tr><th>Product</th><th>Sale Price</th><th>Original</th><th>Off</th><th>Store</th><th></th></tr>
              </thead>
              <tbody>
                {extractedDeals.map((deal, i) => (
                  <tr key={i}>
                    <td>{deal.name}</td>
                    <td className="admin-td-price">${deal.salePrice.toFixed(2)}</td>
                    <td>{deal.originalPrice ? `$${deal.originalPrice.toFixed(2)}` : '—'}</td>
                    <td>{deal.discount ? `${deal.discount}%` : '—'}</td>
                    <td>{deal.store}</td>
                    <td>
                      <button className="btn-admin-remove" onClick={() => removeExtractedDeal(i)} title="Remove">✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
